import { css } from '@emotion/core';
import { Collapse, Icon } from 'antd';
import Link from 'next/link';
import PropTypes from 'prop-types';
import React from 'react';

import contentStyle from '@/src/cms/contentStyle';
import Accordion from '@/src/shared/components/Accordion';
import Sanitize from '@/src/shared/components/Sanitize';
import Table from '@/src/shared/components/Table';
import { bp } from '@/styles';

const { Panel } = Collapse;

const styles = {
  table: css`
    @media screen and (max-width: ${bp.sizes.sm}px) {
      td {
        &:nth-child(1) {
          min-width: 250px;
        }
        &:nth-child(2) {
          min-width: 450px;
        }
      }
    }
  `,
  header: css`
    @media ${bp.sm} {
      display: flex;
      justify-content: space-between;
    }
  `,
  link: css`
    font-size: 1.2rem;
    padding: 1.6rem 0 0;

    @media ${bp.sm} {
      padding: 0;
    }
  `,
};

const columns = [
  {
    title: <b>Topic</b>,
    dataIndex: 'ChartTopic',
    key: 'ChartTopic',
    width: 250,
  },
  {
    title: <b>Details</b>,
    dataIndex: 'Details',
    key: 'Details',
    render: value => {
      return <Sanitize css={contentStyle}>{value}</Sanitize>;
    },
  },
];

const changeColumns = [
  {
    title: <b>Topic</b>,
    dataIndex: 'ChartTopic',
    key: 'ChartTopic',
    width: 250,
  },
  {
    title: <b>Recent Changes</b>,
    dataIndex: 'RecentChanges',
    key: 'RecentChanges',
    render: value => {
      return <Sanitize css={contentStyle}>{value}</Sanitize>;
    },
  },
];

const filterCollapseProps = {
  bordered: false,
  expandIconPosition: 'right',
  expandIcon: ({ isActive }) => <Icon type={isActive ? 'minus' : 'plus'} />,
};

const chartCollapseProps = {
  ...filterCollapseProps,
  expandIconPosition: 'left',
};

const SearchResultsAccordion = ({ results, groupByChart, displayChanges }) => {
  return (
    <div>
      {groupByChart
        ? results.map((chart, index) => (
            <Accordion
              key={`${(index + 1).toString()}-compliassist-accordion`}
              id={(index + 1).toString()}
              panels={[
                {
                  header: `${chart.ChartTitle}`,
                  content: {
                    type: 'generic',
                    items: [
                      <Collapse
                        {...chartCollapseProps}
                        key={`${chart.ChartId}`}
                        id={`${chart.ChartId}`}
                      >
                        {chart.states.map(chartState => {
                          return (
                            <Panel
                              id="chart-result-header"
                              forceRender
                              header={
                                <div css={styles.header}>
                                  <span>
                                    {chartState.ChartState} ({chartState.topics.length} Results)
                                  </span>
                                  <div css={styles.link}>
                                    <Link
                                      href={{
                                        pathname: '/compliance/compliassist/summary',
                                        query: {
                                          chartId: chart.ChartId,
                                          stateCode: chartState.StateCode,
                                        },
                                      }}
                                      onClick={e => e.stopPropagation()}
                                    >
                                      <span id="chart-result-link">
                                        <Icon type="info-circle" theme="filled" />
                                        See Full Details
                                      </span>
                                    </Link>
                                  </div>
                                </div>
                              }
                              key={chartState.ChartStateId.toString()}
                            >
                              <div>
                                <Table
                                  css={styles.table}
                                  rowKey={record => {
                                    return `compliassist-state-topic-row-${record.StateTopicId}`;
                                  }}
                                  pagination={false}
                                  dataSource={chartState.topics}
                                  columns={displayChanges ? changeColumns : columns}
                                />
                              </div>
                            </Panel>
                          );
                        })}
                      </Collapse>,
                    ],
                  },
                },
              ]}
            />
          ))
        : results.map((state, index) => (
            <Accordion
              key={`${(index + 1).toString()}-compliassist-accordion`}
              id={(index + 1).toString()}
              panels={[
                {
                  header: `${state.ChartState}`,
                  content: {
                    type: 'generic',
                    items: [
                      <Collapse
                        {...chartCollapseProps}
                        key={`${state.StateCode}`}
                        id={`${state.StateCode}`}
                      >
                        {state.charts.map(stateChart => {
                          return (
                            <Panel
                              forceRender
                              header={<span>{stateChart.ChartTitle}</span>}
                              key={stateChart.ChartId.toString()}
                            >
                              <div>
                                <Table
                                  css={styles.table}
                                  rowKey={record => {
                                    return `compliassist-state-topic-row-${record.StateTopicId}`;
                                  }}
                                  pagination={false}
                                  dataSource={stateChart.topics}
                                  columns={displayChanges ? changeColumns : columns}
                                />
                              </div>
                            </Panel>
                          );
                        })}
                      </Collapse>,
                    ],
                  },
                },
              ]}
            />
          ))}
    </div>
  );
};

SearchResultsAccordion.propTypes = {
  results: PropTypes.array.isRequired,
};

export default SearchResultsAccordion;
